/**
 * Analytics event catalog.
 *
 * This is the source of truth for TypeScript analytics capture: logical event
 * names, their owning first-party ClickHouse table, bounded property schemas,
 * and PostHog names where the stores differ. Event properties describe only
 * facts observed at request time; durable product state belongs in its owning
 * Postgres or ClickHouse table and should be joined at read time.
 */

import { FIRST_TOUCH_PROPERTY_MAX_BYTES } from "./first-touch-properties";

export const QUERY_ACTIVITY_TEXT_MAX_BYTES = 8 * 1024;

export type AnalyticsEventTable = "web_conversions" | "platform_events" | "cli_events";

type StringProperty = {
  type: "string";
  maxBytes: number;
  optional?: true;
  nullable?: true;
  values?: readonly string[];
};

type NumberProperty = {
  type: "number";
  optional?: true;
  nullable?: true;
  integer?: true;
  min?: number;
  max?: number;
};

type BooleanProperty = { type: "boolean"; optional?: true };

type StringArrayProperty = {
  type: "string[]";
  maxItems: number;
  maxItemBytes: number;
  optional?: true;
};

type PropertyDefinition = StringProperty | NumberProperty | BooleanProperty | StringArrayProperty;

type EventDefinition = {
  table: AnalyticsEventTable;
  clickHouse: string;
  postHog?: string;
  parity?: true;
  properties: Record<string, PropertyDefinition>;
  /**
   * Legacy PostHog-only events still accept bounded JSON properties while
   * callers migrate to explicit fields. First-party events are always strict.
   */
  additionalProperties?: { maxKeys: number; maxBytes: number };
};

const optionalString = (maxBytes: number) =>
  ({
    type: "string",
    maxBytes,
    optional: true,
  }) as const;
const optionalNullableString = (maxBytes: number) =>
  ({
    type: "string",
    maxBytes,
    optional: true,
    nullable: true,
  }) as const;

const boundedLegacyProperties = { maxKeys: 32, maxBytes: 8 * 1024 } as const;

function defineCatalog<const Catalog extends Record<string, EventDefinition>>(catalog: Catalog) {
  return catalog;
}

export const ANALYTICS_EVENT_CATALOG = defineCatalog({
  "account.created": {
    table: "platform_events",
    clickHouse: "account.created",
    postHog: "user_signed_up",
    parity: true,
    properties: {
      source: optionalString(128),
      email: optionalString(320),
      clerk_user_id: optionalString(255),
      $insert_id: optionalString(255),
      org_id: optionalNullableString(255),
      cid: optionalString(FIRST_TOUCH_PROPERTY_MAX_BYTES.cid),
      landing_page: optionalString(FIRST_TOUCH_PROPERTY_MAX_BYTES.landing_page),
      referrer: optionalNullableString(FIRST_TOUCH_PROPERTY_MAX_BYTES.referrer),
      utm_source: optionalNullableString(FIRST_TOUCH_PROPERTY_MAX_BYTES.utm_source),
      utm_medium: optionalNullableString(FIRST_TOUCH_PROPERTY_MAX_BYTES.utm_medium),
      utm_campaign: optionalNullableString(FIRST_TOUCH_PROPERTY_MAX_BYTES.utm_campaign),
      utm_term: optionalNullableString(FIRST_TOUCH_PROPERTY_MAX_BYTES.utm_term),
      utm_content: optionalNullableString(FIRST_TOUCH_PROPERTY_MAX_BYTES.utm_content),
      first_seen_at: optionalString(FIRST_TOUCH_PROPERTY_MAX_BYTES.first_seen_at),
    },
  },
  "api_key.created": {
    table: "platform_events",
    clickHouse: "api_key.created",
    postHog: "api_key.created",
    properties: {
      key_type: {
        type: "string",
        maxBytes: 32,
        values: ["system", "user", "organization"],
      },
      source: {
        type: "string",
        maxBytes: 32,
        values: ["settings", "get_started", "cli_auth_exchange"],
      },
      expiration: {
        type: "string",
        maxBytes: 8,
        optional: true,
        values: ["1d", "7d", "30d", "90d", "1y"],
      },
      expiration_seconds: {
        type: "number",
        optional: true,
        nullable: true,
        integer: true,
        min: 0,
        max: Number.MAX_SAFE_INTEGER,
      },
    },
  },
  "experiment.created": {
    table: "platform_events",
    clickHouse: "experiment.created",
    postHog: "experiment.created",
    parity: true,
    properties: {
      org_id: optionalString(255),
      experiment_id: optionalString(255),
    },
  },
  "onboarding.step_completed": {
    table: "platform_events",
    clickHouse: "onboarding.step_completed",
    postHog: "onboarding.step_completed",
    properties: {
      step_id: {
        type: "string",
        maxBytes: 32,
        values: ["account", "cli-install", "first-experiment"],
      },
      completion_source: {
        type: "string",
        maxBytes: 32,
        values: [
          "api_key_used",
          "cli_credentials_minted",
          "experiment_created",
          "experiment_uploaded",
        ],
      },
    },
  },
  "org.created": {
    table: "platform_events",
    clickHouse: "org.created",
    postHog: "org_created",
    parity: true,
    properties: {
      source: {
        type: "string",
        maxBytes: 64,
        values: ["clerk.organization_created"],
      },
      clerk_user_id: optionalString(255),
      clerk_organization_id: { type: "string", maxBytes: 255 },
      organization_name: optionalString(255),
      $insert_id: optionalString(255),
    },
  },
  "org.member.added": {
    table: "platform_events",
    clickHouse: "org.member.added",
    postHog: "org_membership_created",
    parity: true,
    properties: {
      source: {
        type: "string",
        maxBytes: 64,
        values: ["clerk.organization_membership_created"],
      },
      email: optionalString(320),
      role: { type: "string", maxBytes: 255 },
      clerk_user_id: { type: "string", maxBytes: 255 },
      clerk_organization_id: { type: "string", maxBytes: 255 },
      organization_name: optionalString(255),
      clerk_membership_id: { type: "string", maxBytes: 255 },
      $insert_id: optionalString(255),
    },
  },
  "org.member.removed": {
    table: "platform_events",
    clickHouse: "org.member.removed",
    postHog: "org_membership_removed",
    parity: true,
    properties: {
      source: {
        type: "string",
        maxBytes: 64,
        values: ["clerk.organization_membership_deleted"],
      },
      email: optionalString(320),
      role: { type: "string", maxBytes: 255 },
      clerk_user_id: { type: "string", maxBytes: 255 },
      clerk_organization_id: { type: "string", maxBytes: 255 },
      organization_name: optionalString(255),
      clerk_membership_id: { type: "string", maxBytes: 255 },
      $insert_id: optionalString(255),
    },
  },
  "org.invite.created": {
    table: "platform_events",
    clickHouse: "org.invite.created",
    postHog: "org_invitation_created",
    parity: true,
    properties: {
      source: {
        type: "string",
        maxBytes: 64,
        values: ["clerk.organization_invitation_created"],
      },
      email: optionalString(320),
      role: { type: "string", maxBytes: 255 },
      clerk_user_id: optionalString(255),
      clerk_organization_id: { type: "string", maxBytes: 255 },
      organization_name: optionalString(255),
      clerk_invitation_id: { type: "string", maxBytes: 255 },
      $insert_id: optionalString(255),
    },
  },
  "org.invite.accepted": {
    table: "platform_events",
    clickHouse: "org.invite.accepted",
    postHog: "org_invitation_accepted",
    parity: true,
    properties: {
      source: {
        type: "string",
        maxBytes: 64,
        values: ["clerk.organization_invitation_accepted"],
      },
      email: optionalString(320),
      role: { type: "string", maxBytes: 255 },
      clerk_user_id: optionalString(255),
      clerk_organization_id: { type: "string", maxBytes: 255 },
      organization_name: optionalString(255),
      clerk_invitation_id: { type: "string", maxBytes: 255 },
      $insert_id: optionalString(255),
    },
  },
  "run.submitted": {
    table: "platform_events",
    clickHouse: "run.submitted",
    postHog: "run.submitted",
    parity: true,
    properties: {
      org_id: optionalString(255),
      run_request_id: optionalString(255),
      experiment_id: optionalString(255),
    },
  },
  "run.completed": {
    table: "platform_events",
    clickHouse: "run.completed",
    postHog: "run.completed",
    parity: true,
    properties: {
      org_id: optionalString(255),
      run_id: optionalString(255),
      run_request_id: optionalString(255),
      exit_reason: optionalNullableString(255),
    },
  },
  "run.failed": {
    table: "platform_events",
    clickHouse: "run.failed",
    postHog: "run.failed",
    parity: true,
    properties: {
      org_id: optionalString(255),
      run_id: optionalString(255),
      run_request_id: optionalString(255),
      exit_reason: optionalNullableString(255),
    },
  },
  "billing.credits_purchased": {
    table: "platform_events",
    clickHouse: "billing.credits_purchased",
    postHog: "billing.credits_purchased",
    properties: {
      checkout_session_id: { type: "string", maxBytes: 255 },
      ledger_id: { type: "string", maxBytes: 255 },
      amount_micros: { type: "string", maxBytes: 64 },
      currency: { type: "string", maxBytes: 16 },
      pack_id: { type: "string", maxBytes: 255 },
      stripe_event_id: { type: "string", maxBytes: 255 },
    },
  },
  "billing.credits_reloaded": {
    table: "platform_events",
    clickHouse: "billing.credits_reloaded",
    postHog: "billing.credits_reloaded",
    properties: {
      invoice_id: { type: "string", maxBytes: 255 },
      billing_cycle_id: { type: "string", maxBytes: 255 },
      ledger_id: { type: "string", maxBytes: 255, nullable: true },
      base_ledger_id: { type: "string", maxBytes: 255, nullable: true },
      amount_micros: { type: "string", maxBytes: 64 },
      base_amount_micros: { type: "string", maxBytes: 64 },
      currency: { type: "string", maxBytes: 16 },
    },
  },
  "billing.credit_admission_denied": {
    table: "platform_events",
    clickHouse: "billing.credit_admission_denied",
    postHog: "billing.credit_admission_denied",
    properties: {
      run_request_id: { type: "string", maxBytes: 255 },
      run_count: { type: "number", integer: true, min: 0, max: Number.MAX_SAFE_INTEGER },
      balance_micros: {
        type: "number",
        integer: true,
        min: Number.MIN_SAFE_INTEGER,
        max: Number.MAX_SAFE_INTEGER,
      },
      required_balance_micros: {
        type: "number",
        integer: true,
        min: 0,
        max: Number.MAX_SAFE_INTEGER,
      },
      rate_micros: { type: "number", integer: true, min: 0, max: Number.MAX_SAFE_INTEGER },
      effective_mode: { type: "string", maxBytes: 16, values: ["enforce"] },
    },
  },
  "billing.plan_upgraded": {
    table: "platform_events",
    clickHouse: "billing.plan_upgraded",
    postHog: "billing.plan_upgraded",
    properties: {
      stripe_subscription_id: { type: "string", maxBytes: 255 },
      plan_key: { type: "string", maxBytes: 32, values: ["pro"] },
      status: { type: "string", maxBytes: 32 },
      period_start: { type: "string", maxBytes: 64 },
      period_end: { type: "string", maxBytes: 64 },
      stripe_event_id: { type: "string", maxBytes: 255 },
    },
  },
  axp_query_sql: {
    table: "platform_events",
    clickHouse: "axp_query_sql",
    postHog: "axp_query_sql",
    properties: {
      source: {
        type: "string",
        maxBytes: 32,
        values: ["api", "cli", "cli-retest", "mcp", "public_share"],
      },
      org_id: { type: "string", maxBytes: 255 },
      run_id: { type: "string", maxBytes: 255, nullable: true },
      experiment_id: { type: "string", maxBytes: 255, nullable: true },
      data_schema: {
        type: "string",
        maxBytes: 32,
        values: ["legacy", "session_data_v1"],
      },
      sql: { type: "string", maxBytes: 256 * 1024 },
      sql_bytes: { type: "number", integer: true, min: 0, max: 256 * 1024 },
      // Invalid limits are themselves failure telemetry, so retain any finite
      // request value rather than making analytics reject the same request.
      limit: { type: "number", min: Number.MIN_SAFE_INTEGER, max: Number.MAX_SAFE_INTEGER },
      row_count: { type: "number", integer: true, min: 0, max: 10_000 },
      elapsed_ms: { type: "number", integer: true, min: 0, max: Number.MAX_SAFE_INTEGER },
      synthetic: { type: "boolean" },
      dry_run: { type: "boolean" },
      capped: { type: "boolean" },
      success: { type: "boolean" },
      error: optionalString(256 * 1024),
    },
  },
  "query.succeeded": {
    table: "platform_events",
    clickHouse: "query.succeeded",
    postHog: "axp_query_sql",
    properties: {
      source: {
        type: "string",
        maxBytes: 32,
        values: ["api", "cli", "mcp", "public_share"],
      },
      query_id: { type: "string", maxBytes: 64 },
      duration_ms: { type: "number", integer: true, min: 0, max: Number.MAX_SAFE_INTEGER },
      outcome: { type: "string", maxBytes: 16, values: ["success"] },
      query_text: { type: "string", maxBytes: QUERY_ACTIVITY_TEXT_MAX_BYTES },
      share_id: optionalString(255),
    },
  },
  "query.failed": {
    table: "platform_events",
    clickHouse: "query.failed",
    postHog: "axp_query_sql",
    properties: {
      source: {
        type: "string",
        maxBytes: 32,
        values: ["api", "cli", "mcp", "public_share"],
      },
      query_id: { type: "string", maxBytes: 64 },
      duration_ms: { type: "number", integer: true, min: 0, max: Number.MAX_SAFE_INTEGER },
      outcome: { type: "string", maxBytes: 16, values: ["failure"] },
      query_text: { type: "string", maxBytes: QUERY_ACTIVITY_TEXT_MAX_BYTES },
      error_message: { type: "string", maxBytes: QUERY_ACTIVITY_TEXT_MAX_BYTES },
      share_id: optionalString(255),
    },
  },
  "cli.command.completed": {
    table: "cli_events",
    clickHouse: "cli.command.completed",
    postHog: "ax_cli_command",
    parity: true,
    properties: {
      command: { type: "string", maxBytes: 255 },
      success: { type: "boolean" },
      duration_ms: { type: "number", integer: true, min: 0, max: 4_294_967_295 },
      cli_version: { type: "string", maxBytes: 64 },
      os: { type: "string", maxBytes: 128 },
    },
  },
  waitlist_modal_opened: {
    table: "web_conversions",
    clickHouse: "waitlist_modal_opened",
    postHog: "waitlist_modal_opened",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  waitlist_form_submitted: {
    table: "web_conversions",
    clickHouse: "waitlist_form_submitted",
    postHog: "waitlist_form_submitted",
    properties: { email: optionalString(320) },
  },
  signup_cta_clicked: {
    table: "web_conversions",
    clickHouse: "signup_cta_clicked",
    postHog: "signup_cta_clicked",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  waitlist_signed_up: {
    table: "platform_events",
    clickHouse: "waitlist_signed_up",
    postHog: "waitlist_signed_up",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  contact_form_submitted: {
    table: "platform_events",
    clickHouse: "contact_form_submitted",
    postHog: "contact_form_submitted",
    properties: { email: optionalString(320) },
  },
  contact_meeting_booked: {
    table: "platform_events",
    clickHouse: "contact_meeting_booked",
    postHog: "contact_meeting_booked",
    properties: { email: optionalString(320) },
  },
  banner_cta_clicked: {
    table: "platform_events",
    clickHouse: "banner_cta_clicked",
    postHog: "banner_cta_clicked",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  banner_dismissed: {
    table: "platform_events",
    clickHouse: "banner_dismissed",
    postHog: "banner_dismissed",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  command_center_opened: {
    table: "platform_events",
    clickHouse: "command_center_opened",
    postHog: "command_center_opened",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  command_center_command_selected: {
    table: "platform_events",
    clickHouse: "command_center_command_selected",
    postHog: "command_center_command_selected",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  axp_org_auth_recovery: {
    table: "platform_events",
    clickHouse: "axp_org_auth_recovery",
    postHog: "axp_org_auth_recovery",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  axp_org_auth_final_failure: {
    table: "platform_events",
    clickHouse: "axp_org_auth_final_failure",
    postHog: "axp_org_auth_final_failure",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  axp_realtime_channel_status: {
    table: "platform_events",
    clickHouse: "axp_realtime_channel_status",
    postHog: "axp_realtime_channel_status",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  axp_realtime_reconnect_scheduled: {
    table: "platform_events",
    clickHouse: "axp_realtime_reconnect_scheduled",
    postHog: "axp_realtime_reconnect_scheduled",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  axp_realtime_gap_catchup: {
    table: "platform_events",
    clickHouse: "axp_realtime_gap_catchup",
    postHog: "axp_realtime_gap_catchup",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  axp_realtime_transport_disconnected: {
    table: "platform_events",
    clickHouse: "axp_realtime_transport_disconnected",
    postHog: "axp_realtime_transport_disconnected",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  axp_realtime_auth_missing: {
    table: "platform_events",
    clickHouse: "axp_realtime_auth_missing",
    postHog: "axp_realtime_auth_missing",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  axp_realtime_channel_open_failed: {
    table: "platform_events",
    clickHouse: "axp_realtime_channel_open_failed",
    postHog: "axp_realtime_channel_open_failed",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  axp_realtime_payload_apply_failed: {
    table: "platform_events",
    clickHouse: "axp_realtime_payload_apply_failed",
    postHog: "axp_realtime_payload_apply_failed",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  axp_run_secrets_consumed: {
    table: "platform_events",
    clickHouse: "axp_run_secrets_consumed",
    postHog: "axp_run_secrets_consumed",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  axp_trace_analysis_created: {
    table: "platform_events",
    clickHouse: "axp_trace_analysis_created",
    postHog: "axp_trace_analysis_created",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  axp_secret_set: {
    table: "platform_events",
    clickHouse: "axp_secret_set",
    postHog: "axp_secret_set",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
  axp_secret_deleted: {
    table: "platform_events",
    clickHouse: "axp_secret_deleted",
    postHog: "axp_secret_deleted",
    properties: {},
    additionalProperties: boundedLegacyProperties,
  },
} as const);

export type AnalyticsEventName = keyof typeof ANALYTICS_EVENT_CATALOG;
export type PlatformEventName = {
  [Name in AnalyticsEventName]: (typeof ANALYTICS_EVENT_CATALOG)[Name]["table"] extends "platform_events"
    ? Name
    : never;
}[AnalyticsEventName];
export type WebConversionEventName = {
  [Name in AnalyticsEventName]: (typeof ANALYTICS_EVENT_CATALOG)[Name]["table"] extends "web_conversions"
    ? Name
    : never;
}[AnalyticsEventName];

type InferProperty<Definition extends PropertyDefinition> = Definition extends StringProperty
  ? Definition["nullable"] extends true
    ? string | null
    : Definition extends { values: readonly (infer Value extends string)[] }
      ? Value
      : string
  : Definition extends NumberProperty
    ? Definition["nullable"] extends true
      ? number | null
      : number
    : Definition extends BooleanProperty
      ? boolean
      : Definition extends StringArrayProperty
        ? string[]
        : never;

type PropertySchema<Name extends AnalyticsEventName> =
  (typeof ANALYTICS_EVENT_CATALOG)[Name]["properties"];
type OptionalPropertyKeys<Name extends AnalyticsEventName> = {
  [Key in keyof PropertySchema<Name>]: PropertySchema<Name>[Key] extends { optional: true }
    ? Key
    : never;
}[keyof PropertySchema<Name>];
type RequiredPropertyKeys<Name extends AnalyticsEventName> = Exclude<
  keyof PropertySchema<Name>,
  OptionalPropertyKeys<Name>
>;

export type AnalyticsEventProperties<Name extends AnalyticsEventName> = {
  [Key in RequiredPropertyKeys<Name>]: PropertySchema<Name>[Key] extends PropertyDefinition
    ? InferProperty<PropertySchema<Name>[Key]>
    : never;
} & {
  [Key in OptionalPropertyKeys<Name>]?: PropertySchema<Name>[Key] extends PropertyDefinition
    ? InferProperty<PropertySchema<Name>[Key]>
    : never;
} & ((typeof ANALYTICS_EVENT_CATALOG)[Name] extends { additionalProperties: unknown }
    ? Record<string, unknown>
    : unknown);

export function postHogEventName(name: AnalyticsEventName): string {
  return ANALYTICS_EVENT_CATALOG[name].postHog;
}

export function isAnalyticsEventName(value: string): value is AnalyticsEventName {
  return Object.hasOwn(ANALYTICS_EVENT_CATALOG, value);
}

export function isWebConversionEventName(value: string): value is WebConversionEventName {
  return isAnalyticsEventName(value) && ANALYTICS_EVENT_CATALOG[value].table === "web_conversions";
}

export function validateAnalyticsEventProperties<Name extends AnalyticsEventName>(
  name: Name,
  properties: unknown,
): asserts properties is AnalyticsEventProperties<Name> {
  if (!isRecord(properties)) {
    throw new Error(`analytics event ${name} properties must be an object`);
  }
  const event = ANALYTICS_EVENT_CATALOG[name];
  const schema: Record<string, PropertyDefinition> = event.properties;
  const keys = Object.keys(properties);
  const additional = "additionalProperties" in event ? event.additionalProperties : undefined;
  if (additional && keys.length > additional.maxKeys) {
    throw new Error(`analytics event ${name} has too many properties`);
  }
  if (additional && utf8Bytes(JSON.stringify(properties)) > additional.maxBytes) {
    throw new Error(`analytics event ${name} properties exceed ${additional.maxBytes} bytes`);
  }

  for (const [key, definition] of Object.entries(schema)) {
    const value = properties[key];
    if (value === undefined) {
      if (!definition.optional) {
        throw new Error(`analytics event ${name} requires property ${key}`);
      }
      continue;
    }
    validateProperty(name, key, value, definition);
  }
  for (const key of keys) {
    if (!(key in schema) && !additional) {
      throw new Error(`analytics event ${name} does not allow property ${key}`);
    }
  }
}

function validateProperty(
  name: AnalyticsEventName,
  key: string,
  value: unknown,
  definition: PropertyDefinition,
): void {
  if (definition.type === "string") {
    if (value === null && definition.nullable) return;
    if (typeof value !== "string") {
      throw new Error(`analytics event ${name} property ${key} must be a string`);
    }
    const bytes = utf8Bytes(value);
    if (bytes > definition.maxBytes) {
      throw new Error(
        `analytics event ${name} property ${key} exceeds ${definition.maxBytes} bytes`,
      );
    }
    if (definition.values && !definition.values.includes(value)) {
      throw new Error(`analytics event ${name} property ${key} has an invalid value`);
    }
    return;
  }
  if (definition.type === "number") {
    if (value === null && definition.nullable) return;
    if (
      typeof value !== "number" ||
      !Number.isFinite(value) ||
      (definition.integer && !Number.isInteger(value)) ||
      (definition.min !== undefined && value < definition.min) ||
      (definition.max !== undefined && value > definition.max)
    ) {
      throw new Error(`analytics event ${name} property ${key} must be a bounded number`);
    }
    return;
  }
  if (definition.type === "boolean") {
    if (typeof value !== "boolean") {
      throw new Error(`analytics event ${name} property ${key} must be a boolean`);
    }
    return;
  }
  if (
    !Array.isArray(value) ||
    value.length > definition.maxItems ||
    value.some((item) => typeof item !== "string" || utf8Bytes(item) > definition.maxItemBytes)
  ) {
    throw new Error(`analytics event ${name} property ${key} must be a bounded string array`);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function utf8Bytes(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

/** Compatibility view for parity jobs; mapping ownership stays in this catalog. */
export const ANALYTICS_EVENT_PAIRS = Object.values(ANALYTICS_EVENT_CATALOG)
  .filter(
    (
      event,
    ): event is typeof event & {
      parity: true;
      postHog: string;
    } => "parity" in event && event.parity === true,
  )
  .map((event) => ({
    postHog: event.postHog,
    clickHouse: event.clickHouse,
    table: event.table,
  }));

export type AnalyticsEventPair = (typeof ANALYTICS_EVENT_PAIRS)[number];

/** Stable call-site names derived from the catalog's logical event keys. */
export const TelemetryEvent = {
  PAGE_VIEW: "$pageview",
  WAITLIST_MODAL_OPENED: "waitlist_modal_opened",
  WAITLIST_FORM_SUBMITTED: "waitlist_form_submitted",
  WAITLIST_SIGNED_UP: "waitlist_signed_up",
  SIGNUP_CTA_CLICKED: "signup_cta_clicked",
  CONTACT_FORM_SUBMITTED: "contact_form_submitted",
  CONTACT_MEETING_BOOKED: "contact_meeting_booked",
  USER_SIGNED_UP: "account.created",
  ORG_CREATED: "org.created",
  ORG_INVITATION_CREATED: "org.invite.created",
  ORG_INVITATION_ACCEPTED: "org.invite.accepted",
  ORG_MEMBERSHIP_CREATED: "org.member.added",
  ORG_MEMBERSHIP_REMOVED: "org.member.removed",
  BANNER_CTA_CLICKED: "banner_cta_clicked",
  BANNER_DISMISSED: "banner_dismissed",
  COMMAND_CENTER_OPENED: "command_center_opened",
  COMMAND_CENTER_COMMAND_SELECTED: "command_center_command_selected",
  ORG_AUTH_RECOVERY: "axp_org_auth_recovery",
  ORG_AUTH_FINAL_FAILURE: "axp_org_auth_final_failure",
  REALTIME_CHANNEL_STATUS: "axp_realtime_channel_status",
  REALTIME_RECONNECT_SCHEDULED: "axp_realtime_reconnect_scheduled",
  REALTIME_GAP_CATCHUP: "axp_realtime_gap_catchup",
  REALTIME_TRANSPORT_DISCONNECTED: "axp_realtime_transport_disconnected",
  REALTIME_AUTH_MISSING: "axp_realtime_auth_missing",
  REALTIME_CHANNEL_OPEN_FAILED: "axp_realtime_channel_open_failed",
  REALTIME_PAYLOAD_APPLY_FAILED: "axp_realtime_payload_apply_failed",
} as const satisfies Record<string, AnalyticsEventName | "$pageview">;
